/**
 * O bloco "Pagamentos" do detalhe da reserva (Fase 5) — e os diálogos que o
 * painel financeiro reaproveita.
 *
 * ---------------------------------------------------------------------------
 * OS BOTÕES VÊM DA MÁQUINA DE PAGAMENTO, NUNCA DE `if` DE TELA
 * ---------------------------------------------------------------------------
 * Capturar/baixar/estornar aparecem quando `transicoesDePagamentoPossiveis`
 * (via helpers de `./api`) declara a transição para o ator EMPRESA. Renovar é
 * criação encadeada (caução AUTORIZADA); sincronizar vale enquanto o status
 * não é terminal. Quem recusa de verdade é a API, com 422 que vira toast.
 *
 * PCI: o número do cartão NUNCA aparece aqui. O formulário pede o TOKEN da
 * tokenização — em desenvolvimento o provedor é FAKE e qualquer token de teste
 * com 8+ caracteres serve, e a dica do campo diz isso.
 */

import {
  MAXIMO_DE_PARCELAS,
  ROTULO_DO_METODO_DE_PAGAMENTO,
  ROTULO_DO_STATUS_DE_TRANSACAO,
  ROTULO_DO_TIPO_DE_TRANSACAO,
  formatBRL,
  fromWire,
  planoDeParcelas,
  type StatusDaReserva,
  type StatusDeTransacao,
} from '@locaveri/shared'
import {
  Banknote,
  Circle,
  CircleCheck,
  CircleX,
  Clock,
  Copy,
  CreditCard,
  LoaderCircle,
  Lock,
  type LucideIcon,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCategoria } from '@/features/frota/api'
import {
  ROTULO_DA_FINALIDADE,
  caucaoPrecisaRenovar,
  podeBaixar,
  podeCapturar,
  podeEstornar,
  podeRenovar,
  podeSincronizar,
  useBaixarTransacao,
  useCapturar,
  useCriarCaucao,
  useCriarVenda,
  useEstornarTransacao,
  useRenovarCaucao,
  useSincronizarTransacao,
  useTransacoesDaReserva,
  type CorpoDeCobranca,
  type Transacao,
} from '@/features/pagamentos/api'
import type { ReservaDetalhe } from '@/features/reservas/api'
import { errorMessage } from '@/lib/api'
import { formatarDataHora } from '@/lib/datas'
import { deCampoDeReaisParaWire, formatarMoedaDoWire } from '@/lib/dinheiro'
import { usePermissao } from '@/lib/permissoes'

// ---------------------------------------------------------------------------
// Badge de status — cor + ícone + rótulo, nunca só cor
// ---------------------------------------------------------------------------

const APRESENTACAO_DA_INTENCAO: Readonly<
  Record<
    'neutra' | 'atencao' | 'sucesso' | 'erro',
    {
      readonly icone: LucideIcon
      readonly variante: BadgeProps['variant']
    }
  >
> = {
  neutra: { icone: Circle, variante: 'neutral' },
  atencao: { icone: Clock, variante: 'warning' },
  sucesso: { icone: CircleCheck, variante: 'success' },
  erro: { icone: CircleX, variante: 'danger' },
}

export function BadgeDeStatusDeTransacao({
  status,
}: {
  readonly status: StatusDeTransacao
}): ReactElement {
  const { rotulo, intencao } = ROTULO_DO_STATUS_DE_TRANSACAO[status]
  const { icone: Icone, variante } = APRESENTACAO_DA_INTENCAO[intencao]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {rotulo}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Parcelamento — a política da categoria lida pelo plano determinístico
// ---------------------------------------------------------------------------

interface OpcaoDeParcela {
  readonly parcelas: number
  readonly rotulo: string
}

/**
 * As opções de parcela que o total comporta sob a política da categoria — a
 * MESMA conta de `planoDeParcelas` que a API aplica (maior resto, em
 * centavos). Wire ilegível degrada para "à vista" em vez de quebrar o dialog.
 */
function opcoesDeParcelas(
  totalWire: string,
  maximoDeParcelas: number,
  valorMinimoDaParcelaWire: string,
): readonly OpcaoDeParcela[] {
  try {
    const total = fromWire(totalWire)
    const minimo = fromWire(valorMinimoDaParcelaWire)
    const teto = planoDeParcelas(total, {
      maximoDeParcelas: Math.min(maximoDeParcelas, MAXIMO_DE_PARCELAS),
      valorMinimoDaParcela: minimo,
    }).parcelas

    return Array.from({ length: teto }, (_ignorado, indice) => {
      const parcelas = indice + 1
      if (parcelas === 1) {
        return { parcelas, rotulo: `À vista — ${formatBRL(total)}` }
      }
      const primeira = planoDeParcelas(total, {
        maximoDeParcelas: parcelas,
        valorMinimoDaParcela: minimo,
      }).valores[0]
      return {
        parcelas,
        rotulo:
          primeira === undefined
            ? `${String(parcelas)}×`
            : `${String(parcelas)}× de ${formatBRL(primeira)}`,
      }
    })
  } catch {
    return [{ parcelas: 1, rotulo: 'À vista' }]
  }
}

// ---------------------------------------------------------------------------
// Formulário de cartão tokenizado (compartilhado por venda, caução e renovação)
// ---------------------------------------------------------------------------

interface DadosDoCartao {
  readonly tokenDoCartao: string
  readonly bandeira: string
  readonly ultimosQuatro: string
  readonly nomeDoPortador: string
}

const CARTAO_VAZIO: DadosDoCartao = {
  tokenDoCartao: '',
  bandeira: '',
  ultimosQuatro: '',
  nomeDoPortador: '',
}

function cartaoValido(cartao: DadosDoCartao): boolean {
  const ultimos = cartao.ultimosQuatro.trim()
  return cartao.tokenDoCartao.trim().length >= 8 && (ultimos === '' || /^\d{4}$/.test(ultimos))
}

/** O corpo que a API espera: campos opcionais vazios ficam de fora. */
function corpoDoCartao(cartao: DadosDoCartao, parcelas: number): CorpoDeCobranca {
  const bandeira = cartao.bandeira.trim()
  const ultimosQuatro = cartao.ultimosQuatro.trim()
  const nomeDoPortador = cartao.nomeDoPortador.trim()

  return {
    metodo: 'CARTAO_CREDITO',
    parcelas,
    tokenDoCartao: cartao.tokenDoCartao.trim(),
    ...(bandeira === '' ? {} : { bandeira }),
    ...(ultimosQuatro === '' ? {} : { ultimosQuatro }),
    ...(nomeDoPortador === '' ? {} : { nomeDoPortador }),
  }
}

function CamposDoCartao({
  cartao,
  aoMudar,
  ocupado,
}: {
  readonly cartao: DadosDoCartao
  readonly aoMudar: (cartao: DadosDoCartao) => void
  readonly ocupado: boolean
}): ReactElement {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="pagamento-token">Token do cartão</Label>
        <Input
          id="pagamento-token"
          value={cartao.tokenDoCartao}
          disabled={ocupado}
          onChange={(evento) => {
            aoMudar({ ...cartao, tokenDoCartao: evento.target.value })
          }}
          placeholder="tok_…"
        />
        <p className="text-xs text-fg-muted">
          O número do cartão nunca passa por aqui: cole o token devolvido pela tokenização no
          provedor. Em desenvolvimento o provedor é FAKE — qualquer token de teste com 8 ou mais
          caracteres serve.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="pagamento-bandeira">Bandeira</Label>
          <Input
            id="pagamento-bandeira"
            value={cartao.bandeira}
            disabled={ocupado}
            onChange={(evento) => {
              aoMudar({ ...cartao, bandeira: evento.target.value })
            }}
            placeholder="Visa"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pagamento-ultimos">Últimos 4 dígitos</Label>
          <Input
            id="pagamento-ultimos"
            value={cartao.ultimosQuatro}
            disabled={ocupado}
            inputMode="numeric"
            maxLength={4}
            className="tabular-money"
            onChange={(evento) => {
              aoMudar({ ...cartao, ultimosQuatro: evento.target.value })
            }}
            placeholder="1234"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pagamento-portador">Nome do portador</Label>
          <Input
            id="pagamento-portador"
            value={cartao.nomeDoPortador}
            disabled={ocupado}
            onChange={(evento) => {
              aoMudar({ ...cartao, nomeDoPortador: evento.target.value })
            }}
            placeholder="Como está no cartão"
          />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dialog de cobrança (venda das diárias / pré-autorização da caução)
// ---------------------------------------------------------------------------

function DialogoDeCobranca({
  reserva,
  finalidade,
  aoFechar,
}: {
  readonly reserva: ReservaDetalhe
  readonly finalidade: 'VENDA' | 'CAUCAO'
  readonly aoFechar: () => void
}): ReactElement {
  const venda = finalidade === 'VENDA'
  // A chave nasce com o dialog: dois cliques são a MESMA intenção (invariante 9).
  const chaveDeIdempotencia = useMemo(() => crypto.randomUUID(), [])

  const [metodo, setMetodo] = useState<'CARTAO_CREDITO' | 'PIX'>(venda ? 'PIX' : 'CARTAO_CREDITO')
  const [parcelas, setParcelas] = useState(1)
  const [cartao, setCartao] = useState<DadosDoCartao>(CARTAO_VAZIO)

  const criarVenda = useCriarVenda(reserva.id)
  const criarCaucao = useCriarCaucao(reserva.id)
  const mutacao = venda ? criarVenda : criarCaucao

  // A política de parcelamento é da CATEGORIA (Fase 5). Se a leitura falhar, o
  // dialog degrada para "à vista" — a API continua sendo quem valida o plano.
  const categoria = useCategoria(venda ? reserva.categoria.id : null)
  const opcoes = useMemo(
    () =>
      categoria.data === undefined
        ? [{ parcelas: 1, rotulo: 'À vista' } as const]
        : opcoesDeParcelas(
            reserva.total,
            categoria.data.maximoDeParcelas,
            categoria.data.valorMinimoDaParcela,
          ),
    [categoria.data, reserva.total],
  )

  const valor = venda ? reserva.total : reserva.caucao
  const pronto = metodo === 'PIX' ? venda : cartaoValido(cartao)

  function enviar(): void {
    const corpo: CorpoDeCobranca =
      metodo === 'PIX'
        ? { metodo: 'PIX', parcelas: 1 }
        : corpoDoCartao(cartao, venda ? parcelas : 1)

    mutacao.mutate(
      { corpo, chaveDeIdempotencia },
      {
        onSuccess: (transacao) => {
          toast.success(
            venda
              ? transacao.metodo === 'PIX'
                ? 'Cobrança PIX criada — o QR aguarda o pagamento do cliente.'
                : 'Venda enviada ao provedor.'
              : 'Caução pré-autorizada — o limite do cliente está bloqueado.',
          )
          aoFechar()
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{venda ? 'Cobrar diárias' : 'Pré-autorizar caução'}</DialogTitle>
          <DialogDescription>
            {venda
              ? `O valor é o TOTAL congelado da reserva (${formatarMoedaDoWire(valor)}) — nada é digitado aqui.`
              : `Bloqueia ${formatarMoedaDoWire(valor)} no limite do cliente, sem cobrar. A captura ou a baixa acontecem no fechamento.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {venda ? (
            <div className="space-y-1.5">
              <Label htmlFor="pagamento-metodo">Método</Label>
              <Select
                id="pagamento-metodo"
                value={metodo}
                disabled={mutacao.isPending}
                onChange={(evento) => {
                  setMetodo(evento.target.value === 'PIX' ? 'PIX' : 'CARTAO_CREDITO')
                }}
              >
                <option value="PIX">{ROTULO_DO_METODO_DE_PAGAMENTO.PIX}</option>
                <option value="CARTAO_CREDITO">
                  {ROTULO_DO_METODO_DE_PAGAMENTO.CARTAO_CREDITO}
                </option>
              </Select>
            </div>
          ) : (
            <p className="text-sm text-fg-secondary">
              Pré-autorização é sempre no cartão de crédito — PIX não bloqueia limite.
            </p>
          )}

          {metodo === 'CARTAO_CREDITO' ? (
            <>
              <CamposDoCartao cartao={cartao} aoMudar={setCartao} ocupado={mutacao.isPending} />
              {venda ? (
                <div className="space-y-1.5">
                  <Label htmlFor="pagamento-parcelas">Parcelas</Label>
                  <Select
                    id="pagamento-parcelas"
                    value={String(parcelas)}
                    disabled={mutacao.isPending}
                    onChange={(evento) => {
                      setParcelas(Number(evento.target.value))
                    }}
                  >
                    {opcoes.map((opcao) => (
                      <option key={opcao.parcelas} value={opcao.parcelas}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-fg-muted">
                    {categoria.isError
                      ? 'Não foi possível ler a política da categoria — cobrando à vista.'
                      : 'O teto vem da política da categoria; os valores somam o total no centavo (maior resto).'}
                  </p>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-fg-secondary">
              A transação nasce aguardando o pagamento: o código copia-e-cola do QR aparece na lista
              para enviar ao cliente.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={aoFechar} disabled={mutacao.isPending}>
            Cancelar
          </Button>
          <Button disabled={!pronto || mutacao.isPending} onClick={enviar}>
            {mutacao.isPending ? <LoaderCircle className="animate-spin" /> : null}
            {venda
              ? `Cobrar ${formatarMoedaDoWire(valor)}`
              : `Bloquear ${formatarMoedaDoWire(valor)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Dialog de renovação de caução (Gap 6) — exportado para o painel financeiro
// ---------------------------------------------------------------------------

export function DialogoDeRenovacaoDeCaucao({
  transacaoId,
  valor,
  aoFechar,
}: {
  readonly transacaoId: string
  /** Wire de centavos, só para o texto do dialog. */
  readonly valor: string
  readonly aoFechar: () => void
}): ReactElement {
  const chaveDeIdempotencia = useMemo(() => crypto.randomUUID(), [])
  const [cartao, setCartao] = useState<DadosDoCartao>(CARTAO_VAZIO)
  const renovar = useRenovarCaucao()

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) {
          aoFechar()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Renovar caução</DialogTitle>
          <DialogDescription>
            Nova pré-autorização de {formatarMoedaDoWire(valor)} com um token novo do cliente —
            autorizada, a antiga é baixada e encadeada. O sistema não guarda token de cartão (PCI),
            por isso a renovação pede um novo.
          </DialogDescription>
        </DialogHeader>

        <CamposDoCartao cartao={cartao} aoMudar={setCartao} ocupado={renovar.isPending} />

        <DialogFooter>
          <Button variant="ghost" onClick={aoFechar} disabled={renovar.isPending}>
            Cancelar
          </Button>
          <Button
            disabled={!cartaoValido(cartao) || renovar.isPending}
            onClick={() => {
              renovar.mutate(
                { id: transacaoId, corpo: corpoDoCartao(cartao, 1), chaveDeIdempotencia },
                {
                  onSuccess: () => {
                    toast.success('Caução renovada — a pré-autorização antiga foi baixada.')
                    aoFechar()
                  },
                  onError: (erro) => {
                    toast.error(errorMessage(erro))
                  },
                },
              )
            }}
          >
            {renovar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Renovar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Dialogs de captura, estorno e baixa
// ---------------------------------------------------------------------------

function DialogoDeCaptura({
  transacao,
  aoFechar,
}: {
  readonly transacao: Transacao
  readonly aoFechar: () => void
}): ReactElement {
  const [valorTexto, setValorTexto] = useState('')
  const capturar = useCapturar()

  const parcial = valorTexto.trim() !== ''
  const valorWire = parcial ? deCampoDeReaisParaWire(valorTexto) : null
  const invalido = parcial && valorWire === null

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) {
          aoFechar()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Capturar a pré-autorização</DialogTitle>
          <DialogDescription>
            A caução (toda ou em parte) vira cobrança. Vazio captura o valor inteiro (
            {formatarMoedaDoWire(transacao.valor)}).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="captura-valor">Valor a capturar (opcional)</Label>
          <Input
            id="captura-valor"
            value={valorTexto}
            inputMode="decimal"
            placeholder="0,00"
            className="tabular-money"
            disabled={capturar.isPending}
            onChange={(evento) => {
              setValorTexto(evento.target.value)
            }}
          />
          {invalido ? (
            <p className="text-xs text-danger-fg">Valor inválido — use o formato 1.500,00.</p>
          ) : (
            <p className="text-xs text-fg-muted">Em reais. Deixe vazio para capturar tudo.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={aoFechar} disabled={capturar.isPending}>
            Cancelar
          </Button>
          <Button
            disabled={invalido || capturar.isPending}
            onClick={() => {
              capturar.mutate(
                { id: transacao.id, valor: valorWire },
                {
                  onSuccess: () => {
                    toast.success('Captura feita — o valor virou cobrança.')
                    aoFechar()
                  },
                  onError: (erro) => {
                    toast.error(errorMessage(erro))
                  },
                },
              )
            }}
          >
            {capturar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Capturar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DialogoDeEstorno({
  transacao,
  aoFechar,
}: {
  readonly transacao: Transacao
  readonly aoFechar: () => void
}): ReactElement {
  const [motivo, setMotivo] = useState('')
  const [valorTexto, setValorTexto] = useState('')
  const estornar = useEstornarTransacao()

  const parcial = valorTexto.trim() !== ''
  const valorWire = parcial ? deCampoDeReaisParaWire(valorTexto) : null
  const valorInvalido = parcial && valorWire === null

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) {
          aoFechar()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Estornar</DialogTitle>
          <DialogDescription>
            Gesto de financeiro: o motivo é obrigatório e vai para a trilha de auditoria. Sem valor,
            estorna tudo o que ainda está cobrado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={motivo}
            disabled={estornar.isPending}
            onChange={(evento) => {
              setMotivo(evento.target.value)
            }}
            placeholder="Motivo do estorno (mínimo de 5 caracteres)…"
          />
          <div className="space-y-1.5">
            <Label htmlFor="estorno-valor">Valor a estornar (opcional)</Label>
            <Input
              id="estorno-valor"
              value={valorTexto}
              inputMode="decimal"
              placeholder="0,00"
              className="tabular-money"
              disabled={estornar.isPending}
              onChange={(evento) => {
                setValorTexto(evento.target.value)
              }}
            />
            {valorInvalido ? (
              <p className="text-xs text-danger-fg">Valor inválido — use o formato 1.500,00.</p>
            ) : (
              <p className="text-xs text-fg-muted">Em reais. Deixe vazio para estorno total.</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={aoFechar} disabled={estornar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={motivo.trim().length < 5 || valorInvalido || estornar.isPending}
            onClick={() => {
              estornar.mutate(
                { id: transacao.id, motivo: motivo.trim(), valor: valorWire },
                {
                  onSuccess: () => {
                    toast.success('Estorno aplicado, com o motivo na trilha.')
                    aoFechar()
                  },
                  onError: (erro) => {
                    toast.error(errorMessage(erro))
                  },
                },
              )
            }}
          >
            {estornar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Estornar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DialogoDeBaixa({
  transacao,
  aoFechar,
}: {
  readonly transacao: Transacao
  readonly aoFechar: () => void
}): ReactElement {
  const baixar = useBaixarTransacao()

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) {
          aoFechar()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Baixar a pré-autorização</DialogTitle>
          <DialogDescription>
            Devolução em ordem: o bloqueio de {formatarMoedaDoWire(transacao.valor)} volta inteiro
            para o limite do cliente, sem cobrar nada. A baixa é terminal.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={aoFechar} disabled={baixar.isPending}>
            Cancelar
          </Button>
          <Button
            disabled={baixar.isPending}
            onClick={() => {
              baixar.mutate(
                { id: transacao.id },
                {
                  onSuccess: () => {
                    toast.success('Pré-autorização baixada — o limite foi devolvido.')
                    aoFechar()
                  },
                  onError: (erro) => {
                    toast.error(errorMessage(erro))
                  },
                },
              )
            }}
          >
            {baixar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Baixar sem cobrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Uma transação na lista
// ---------------------------------------------------------------------------

function copiarQr(qr: string): void {
  void navigator.clipboard.writeText(qr).then(
    () => {
      toast.success('Código PIX copiado.')
    },
    () => {
      toast.error('Não foi possível copiar o código.')
    },
  )
}

type AcaoDeLinha = 'capturar' | 'baixar' | 'estornar' | 'renovar'

function LinhaDeTransacao({
  transacao,
  aoAgir,
}: {
  readonly transacao: Transacao
  readonly aoAgir: (acao: AcaoDeLinha) => void
}): ReactElement {
  const podeAtualizar = usePermissao('payment.update')
  const podeCriar = usePermissao('payment.create')
  const sincronizar = useSincronizarTransacao()

  const renovacaoUrgente = caucaoPrecisaRenovar(transacao)
  const cartao =
    transacao.metodo === 'CARTAO_CREDITO'
      ? `${transacao.bandeira ?? 'cartão'} •••• ${transacao.ultimosQuatro ?? '????'}`
      : null

  return (
    <div className="space-y-2 rounded-md border border-border p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{ROTULO_DA_FINALIDADE[transacao.finalidade]}</span>
          <BadgeDeStatusDeTransacao status={transacao.status} />
        </div>
        <span className="money-cell font-semibold text-foreground">
          {formatarMoedaDoWire(transacao.valor)}
        </span>
      </div>

      <p className="text-xs text-fg-secondary">
        {ROTULO_DO_TIPO_DE_TRANSACAO[transacao.tipo]} ·{' '}
        {ROTULO_DO_METODO_DE_PAGAMENTO[transacao.metodo]}
        {cartao === null ? '' : ` · ${cartao}`}
        {transacao.parcelas > 1 ? ` · ${String(transacao.parcelas)}×` : ''} ·{' '}
        {formatarDataHora(transacao.criadoEm)}
      </p>

      {transacao.valorCapturado !== '0' ? (
        <p className="text-xs text-fg-secondary">
          Capturado:{' '}
          <span className="tabular-money">{formatarMoedaDoWire(transacao.valorCapturado)}</span>
        </p>
      ) : null}
      {transacao.valorEstornado !== '0' ? (
        <p className="text-xs text-fg-secondary">
          Estornado:{' '}
          <span className="tabular-money">{formatarMoedaDoWire(transacao.valorEstornado)}</span>
        </p>
      ) : null}

      {transacao.autorizacaoExpiraEm === null ? null : (
        <div className="flex flex-wrap items-center gap-2 text-xs text-fg-secondary">
          <span>
            Pré-autorização válida até{' '}
            <span className="tabular-money">{formatarDataHora(transacao.autorizacaoExpiraEm)}</span>
          </span>
          {renovacaoUrgente ? (
            <Badge variant="danger">
              <TriangleAlert aria-hidden="true" />
              Renovar caução
            </Badge>
          ) : null}
        </div>
      )}

      {transacao.status === 'AGUARDANDO_CONFIRMACAO' && transacao.qrCode !== null ? (
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-elevated px-2 py-1 text-xs">
            {transacao.qrCode}
          </code>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={() => {
              copiarQr(transacao.qrCode ?? '')
            }}
          >
            <Copy aria-hidden="true" />
            Copiar PIX
          </Button>
        </div>
      ) : null}

      {transacao.mensagemDoProvedor === null ? null : (
        <p className="text-xs text-fg-muted">
          Provedor: {transacao.mensagemDoProvedor}
          {transacao.codigoDoProvedor === null ? '' : ` (código ${transacao.codigoDoProvedor})`}
        </p>
      )}
      {transacao.motivo === null ? null : (
        <p className="text-xs text-fg-muted">Motivo: “{transacao.motivo}”</p>
      )}

      <div className="flex flex-wrap gap-2">
        {podeAtualizar && podeCapturar(transacao) ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              aoAgir('capturar')
            }}
          >
            Capturar…
          </Button>
        ) : null}
        {podeAtualizar && podeBaixar(transacao) ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              aoAgir('baixar')
            }}
          >
            Baixar…
          </Button>
        ) : null}
        {podeAtualizar && podeEstornar(transacao) ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              aoAgir('estornar')
            }}
          >
            Estornar…
          </Button>
        ) : null}
        {podeCriar && podeRenovar(transacao) ? (
          <Button
            size="sm"
            variant={renovacaoUrgente ? 'default' : 'outline'}
            onClick={() => {
              aoAgir('renovar')
            }}
          >
            Renovar caução…
          </Button>
        ) : null}
        {podeAtualizar && podeSincronizar(transacao) ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={sincronizar.isPending}
            onClick={() => {
              sincronizar.mutate(
                { id: transacao.id },
                {
                  onSuccess: () => {
                    toast.success('Estado convergido com o provedor.')
                  },
                  onError: (erro) => {
                    toast.error(errorMessage(erro))
                  },
                },
              )
            }}
          >
            {sincronizar.isPending ? (
              <LoaderCircle className="animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw aria-hidden="true" />
            )}
            Sincronizar
          </Button>
        ) : null}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// O cartão da reserva
// ---------------------------------------------------------------------------

/** Status da reserva que aceitam pré-autorizar a caução (o mesmo set da API). */
const STATUS_QUE_ACEITAM_CAUCAO: ReadonlySet<StatusDaReserva> = new Set([
  'APROVADA',
  'VEICULO_ALOCADO',
  'RETIRADA_PENDENTE',
  'EM_ANDAMENTO',
])

/** Uma caução "viva" (nem baixada, nem expirada) já cobre a reserva. */
function temCaucaoViva(transacoes: readonly Transacao[]): boolean {
  return transacoes.some(
    (transacao) =>
      (transacao.finalidade === 'CAUCAO' || transacao.finalidade === 'RENOVACAO_DE_CAUCAO') &&
      (transacao.status === 'CRIADA' ||
        transacao.status === 'AGUARDANDO_CONFIRMACAO' ||
        transacao.status === 'AUTORIZADA'),
  )
}

export function CartaoDePagamentos({
  reserva,
}: {
  readonly reserva: ReservaDetalhe
}): ReactElement | null {
  const podeLer = usePermissao('payment.read')
  const podeCriar = usePermissao('payment.create')
  const consulta = useTransacoesDaReserva(podeLer ? reserva.id : null)

  const [cobranca, setCobranca] = useState<'VENDA' | 'CAUCAO' | null>(null)
  const [acao, setAcao] = useState<{ tipo: AcaoDeLinha; transacao: Transacao } | null>(null)

  if (!podeLer) {
    return null
  }

  const itens = consulta.data?.itens ?? []
  const mostrarVenda = podeCriar && reserva.status === 'AGUARDANDO_PAGAMENTO'
  const mostrarCaucao =
    podeCriar && STATUS_QUE_ACEITAM_CAUCAO.has(reserva.status) && !temCaucaoViva(itens)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="size-4" /> Pagamentos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {consulta.isPending ? <LoadingState rotulo="Carregando os pagamentos…" /> : null}
        {consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar os pagamentos"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : null}

        {!consulta.isPending && !consulta.isError && itens.length === 0 ? (
          <EmptyState
            icone={CreditCard}
            titulo="Nenhuma transação"
            descricao={
              mostrarVenda
                ? 'A reserva aguarda pagamento — cobre as diárias pelo total congelado.'
                : 'As cobranças e a caução desta reserva aparecem aqui quando existirem.'
            }
          />
        ) : null}

        {itens.map((transacao) => (
          <LinhaDeTransacao
            key={transacao.id}
            transacao={transacao}
            aoAgir={(tipo) => {
              setAcao({ tipo, transacao })
            }}
          />
        ))}

        {mostrarVenda || mostrarCaucao ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {mostrarVenda ? (
              <Button
                onClick={() => {
                  setCobranca('VENDA')
                }}
              >
                <Banknote aria-hidden="true" />
                Cobrar diárias
              </Button>
            ) : null}
            {mostrarCaucao ? (
              <Button
                variant="outline"
                onClick={() => {
                  setCobranca('CAUCAO')
                }}
              >
                <Lock aria-hidden="true" />
                Pré-autorizar caução
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>

      {cobranca === null ? null : (
        <DialogoDeCobranca
          reserva={reserva}
          finalidade={cobranca}
          aoFechar={() => {
            setCobranca(null)
          }}
        />
      )}

      {acao?.tipo === 'capturar' ? (
        <DialogoDeCaptura
          transacao={acao.transacao}
          aoFechar={() => {
            setAcao(null)
          }}
        />
      ) : null}
      {acao?.tipo === 'baixar' ? (
        <DialogoDeBaixa
          transacao={acao.transacao}
          aoFechar={() => {
            setAcao(null)
          }}
        />
      ) : null}
      {acao?.tipo === 'estornar' ? (
        <DialogoDeEstorno
          transacao={acao.transacao}
          aoFechar={() => {
            setAcao(null)
          }}
        />
      ) : null}
      {acao?.tipo === 'renovar' ? (
        <DialogoDeRenovacaoDeCaucao
          transacaoId={acao.transacao.id}
          valor={acao.transacao.valor}
          aoFechar={() => {
            setAcao(null)
          }}
        />
      ) : null}
    </Card>
  )
}
